'use client';

/**
 * Cart store — carrito por vendedor con estado global compartido.
 *
 * v136 fix: useCart() usaba useState local — cada componente que llamaba
 * useCart() obtenía su PROPIA instancia del estado. CartFloatingButton hacía
 * setIsOpen(true) en su estado local, pero CartDrawer tenía su propio
 * isOpen=false. El drawer nunca se abría.
 *
 * Fix de raíz: usar un patrón singleton con suscriptores (useSyncExternalStore)
 * para que TODOS los componentes que llaman useCart() compartan el mismo estado.
 *
 * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): extended CartItem with the
 * product's `fulfillment` config + `sellerFulfillmentOptions` snapshot. The
 * CartCheckout modal needs this data to display shipping options + costs +
 * free-shipping threshold (parity with the "Comprar ahora" modal). Without
 * these fields, CartCheckout had to re-fetch each product from the BE on
 * checkout — slow + racy. Now the data is captured at addItem time.
 */

import { useSyncExternalStore, useCallback } from 'react';
import type {
  ProductFulfillment,
  PublicProductResponse,
} from '@profileia/shared';

/**
 * Snapshot of seller fulfillment options captured at addItem time.
 * Mirrors `PublicProductResponse['seller']['fulfillmentOptions']` — only
 * the fields CartCheckout needs to compute shipping + pickup display.
 */
export interface CartSellerFulfillmentOptions {
  ships: boolean;
  pickup: boolean;
  shipping: {
    methods: string[];
    cost: string;
    freeShippingThreshold: string | null;
    minOrderCredits: string;
  } | null;
  pickupConfig: {
    location: { lat: number; lng: number; address: string };
    instructions: string;
    hours: string;
  } | null;
}

export interface CartItem {
  productId: string;
  productTitle: string;
  productSlug: string;
  storeSlug: string;
  sellerId: string;
  sellerName: string;
  priceCredits: string;
  quantity: number;
  image?: string;
  type: 'physical' | 'digital' | 'service';
  variationSelections?: Array<{ name: string; option: string }>;
  /**
   * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): the product's fulfillment
   * config (inheritSeller, modalities, config.shipping, config.pickup,
   * shippingOverride). Captured at addItem time so CartCheckout can compute
   * shipping costs + free-shipping threshold using `resolveShipping()`.
   * Required for physical items; absent for digital/service items (they
   * don't ship).
   */
  fulfillment?: ProductFulfillment;
  /**
   * Snapshot of the seller's fulfillment options (ships, pickup, shipping,
   * pickupConfig). Captured at addItem time so CartCheckout can resolve
   * the effective shipping config (seller default when inheritSeller=true).
   */
  sellerFulfillmentOptions?: CartSellerFulfillmentOptions;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

const STORAGE_KEY = 'profileia.cart';

/**
 * Migrate a localStorage-loaded CartItem to the new schema. Pre-fix
 * items don't have `fulfillment` / `sellerFulfillmentOptions` — the
 * CartCheckout will gracefully degrade (no shipping display) when these
 * fields are absent. The user can simply re-add the item to get the
 * new fields populated.
 *
 * Anti-P-013: silent migration — old items are NOT deleted (the user's
 * cart contents are preserved). The missing fields are just `undefined`.
 */
function migrateItem(item: CartItem): CartItem {
  // No-op for items that already have the new fields. For old items,
  // the fields are simply left undefined — CartCheckout handles the
  // absence gracefully (renders a "Re-fetch shipping info" callout
  // or falls back to ship-only with hidden cost).
  return item;
}

// ─── Singleton store ─────────────────────────────────────────────────────

let state: CartState = { items: [], isOpen: false };
const listeners = new Set<() => void>();

function loadCart(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): migrate each item to
    // the new schema (fills in default undefined for new fields).
    return parsed.map((item: CartItem) => migrateItem(item));
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage disabled (private mode) — fail silently
  }
}

function setState(next: Partial<CartState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Load cart from localStorage on first subscribe
  if (state.items.length === 0 && typeof window !== 'undefined') {
    const loaded = loadCart();
    if (loaded.length > 0) {
      state = { ...state, items: loaded };
    }
  }
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

// ─── Actions ─────────────────────────────────────────────────────────────

function addItem(item: CartItem) {
  const existing = state.items.find(
    (i) => i.productId === item.productId && i.sellerId === item.sellerId,
  );
  let nextItems: CartItem[];
  if (existing) {
    nextItems = state.items.map((i) =>
      i.productId === item.productId && i.sellerId === item.sellerId
        ? { ...i, quantity: i.quantity + item.quantity }
        : i,
    );
  } else {
    nextItems = [...state.items, item];
  }
  saveCart(nextItems);
  setState({ items: nextItems, isOpen: true });
}

/**
 * TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): convenience helper that
 * accepts a `PublicProductResponse` and builds a `CartItem` with all the
 * new fields (fulfillment + sellerFulfillmentOptions) populated. The
 * caller doesn't have to manually pluck the right fields.
 *
 * Captures ONLY the data CartCheckout needs to compute shipping + pickup
 * display — not the entire product object (keeps localStorage small).
 *
 * Anti-P-382: reuses the existing `addItem` (no parallel code path).
 */
function addItemFromProduct(
  product: PublicProductResponse,
  options?: { quantity?: number; variationSelections?: Array<{ name: string; option: string }> },
): void {
  const qty = options?.quantity ?? 1;
  // Snapshot ONLY the seller fulfillment fields CartCheckout needs (avoid
  // bloating localStorage with the entire seller object — bookingConfig,
  // serviceConfig, etc. are NOT needed for shipping display).
  const sellerOpts: CartSellerFulfillmentOptions = {
    ships: product.seller.fulfillmentOptions.ships,
    pickup: product.seller.fulfillmentOptions.pickup,
    shipping: product.seller.fulfillmentOptions.shipping
      ? {
          methods: product.seller.fulfillmentOptions.shipping.methods,
          cost: product.seller.fulfillmentOptions.shipping.cost,
          freeShippingThreshold: product.seller.fulfillmentOptions.shipping.freeShippingThreshold,
          minOrderCredits: product.seller.fulfillmentOptions.shipping.minOrderCredits,
        }
      : null,
    pickupConfig: product.seller.fulfillmentOptions.pickupConfig
      ? {
          location: product.seller.fulfillmentOptions.pickupConfig.location,
          instructions: product.seller.fulfillmentOptions.pickupConfig.instructions,
          hours: product.seller.fulfillmentOptions.pickupConfig.hours,
        }
      : null,
  };
  addItem({
    productId: product.productId,
    productTitle: product.title,
    productSlug: product.slug,
    storeSlug: product.seller.storeSlug,
    sellerId: product.seller.storeSlug,
    sellerName: product.seller.storeName,
    priceCredits: product.priceCredits,
    quantity: qty,
    image: product.images[0],
    type: product.type,
    variationSelections: options?.variationSelections,
    fulfillment: product.fulfillment,
    sellerFulfillmentOptions: sellerOpts,
  });
}

function removeItem(productId: string, sellerId: string) {
  const nextItems = state.items.filter(
    (i) => !(i.productId === productId && i.sellerId === sellerId),
  );
  saveCart(nextItems);
  setState({ items: nextItems });
}

function updateQuantity(productId: string, sellerId: string, quantity: number) {
  if (quantity < 1) return;
  const nextItems = state.items.map((i) =>
    i.productId === productId && i.sellerId === sellerId
      ? { ...i, quantity }
      : i,
  );
  saveCart(nextItems);
  setState({ items: nextItems });
}

function clearBySeller(sellerId: string) {
  const nextItems = state.items.filter((i) => i.sellerId !== sellerId);
  saveCart(nextItems);
  setState({ items: nextItems });
}

function clearAll() {
  saveCart([]);
  setState({ items: [], isOpen: false });
}

function setIsOpen(open: boolean) {
  setState({ isOpen: open });
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useCart() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const itemsBySeller = snapshot.items.reduce(
    (acc, item) => {
      if (!acc[item.sellerId]) acc[item.sellerId] = [];
      acc[item.sellerId].push(item);
      return acc;
    },
    {} as Record<string, CartItem[]>,
  );

  const totalBySeller = Object.entries(itemsBySeller).reduce(
    (acc, [sellerId, sellerItems]) => {
      acc[sellerId] = sellerItems
        .reduce(
          (sum, i) => sum + parseFloat(i.priceCredits) * i.quantity,
          0,
        )
        .toFixed(2);
      return acc;
    },
    {} as Record<string, string>,
  );

  const totalCount = snapshot.items.reduce((sum, i) => sum + i.quantity, 0);
  const grandTotal = snapshot.items
    .reduce((sum, i) => sum + parseFloat(i.priceCredits) * i.quantity, 0)
    .toFixed(2);

  return {
    items: snapshot.items,
    itemsBySeller,
    totalBySeller,
    totalCount,
    grandTotal,
    isOpen: snapshot.isOpen,
    setIsOpen,
    addItem,
    // TASK-FEAT-CART-UNIFIED-CHECKOUT (2026-11-05): convenience helper.
    addItemFromProduct,
    removeItem,
    updateQuantity,
    clearBySeller,
    clearAll,
  };
}
